import api from "../../../core/api";
import { ServiceReport } from "../../machinery/models/ServiceReportsModel";
import { ServiceRequest } from "../../servicing/models/models";
import { WorkOrder } from "../models/models";
import { Page } from "../../../shared/models/models";

// ─── Machinery (Service Reports) ────────────────────────────
const getMachineryConfig = () => ({
  baseURL: (window as any).app.config.machineryApiBaseUrl,
});

// ─── Servicing (RFS / Service Requests) ─────────────────────
const getServicingConfig = () => ({
  baseURL: (window as any).app.config.servicingApiBaseUrl,
});

// ─── Maintenance (Work Orders) ──────────────────────────────
const getMaintenanceConfig = () => ({
  baseURL: (window as any).app.config.maintenanceApiBaseUrl,
});

const serializeParams = (params: any): string => {
  const parts: string[] = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      value.forEach((item) => {
        parts.push(`${key}=${encodeURIComponent(item)}`);
      });
    } else if (value !== undefined && value !== null) {
      parts.push(`${key}=${encodeURIComponent(value)}`);
    }
  });
  return parts.join("&");
};

/** DTO returned by the company-wide SR endpoint (includes machine context). */
export interface CompanyServiceReport extends ServiceReport {
  machineId?: string;
  machineInternalNumber?: string;
  machineSerialNumber?: string;
  machineModel?: string;
  manufacturerName?: string;
  locationId?: string;
  locationName?: string;
  parentLocationName?: string;
  createdDate?: string;
  createdViaMidas?: boolean;
  fileCount?: number;
}

class ServiceReportsApi {
  /**
   * Fetch a single SR detail with full file content (base64).
   * The per-machine endpoint returns files with content, unlike the company-wide endpoint.
   */
  async getServiceReportDetail(machineId: string, reportNumber: string): Promise<ServiceReport | null> {
    try {
      const response = await api.get<Page<ServiceReport>>(
        `/v1/customer/machines/${machineId}/service-reports`,
        {
          ...getMachineryConfig(),
          params: { search: reportNumber, pageSize: 1 },
          paramsSerializer: serializeParams,
        }
      );
      return response.data.items?.[0] || null;
    } catch {
      return null;
    }
  }

  /**
   * Fetch all SRs across the company.
   * TODO: SR-1B — replace with /v1/customer/service-reports/company when deployed.
   * For now, falls back to fetching per-machine SRs or returns empty on 404.
   */
  async getCompanyServiceReports(): Promise<CompanyServiceReport[]> {
    try {
      const response = await api.get<any>(
        "/v1/customer/service-reports/company",
        {
          ...getMachineryConfig(),
          params: { pageSize: 500 },
          paramsSerializer: serializeParams,
        }
      );
      // Handle both raw array and paginated { items: [...] } response shapes
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data?.items && Array.isArray(data.items)) return data.items;
      // Unexpected shape — return empty
      console.warn("[ServiceReports] Unexpected response shape:", typeof data);
      return [];
    } catch (err: any) {
      const status = err?.response?.status;
      // 404 = endpoint not deployed yet (SR-1B) — return empty gracefully
      if (status === 404) {
        console.warn("[ServiceReports] Company endpoint not available (404)");
        return [];
      }
      // 403 = permission denied — surface the error so the user sees it
      if (status === 403) {
        console.error("[ServiceReports] Permission denied (403) — user lacks Machinery.Read or Maintenance.Read");
        throw new Error("Permission denied: you don't have access to service reports.");
      }
      // Other errors — log and re-throw so the slice captures the error state
      console.error("[ServiceReports] Failed to fetch company service reports:", status || err?.message);
      throw err;
    }
  }

  /**
   * Fetch all Work Orders (for joining SRs to WOs via serviceReportId).
   * Re-uses the existing WO list endpoint with a large page.
   */
  async getAllWorkOrders(): Promise<WorkOrder[]> {
    try {
      const response = await api.get<Page<WorkOrder>>("/v1/customer/work-orders", {
        ...getMaintenanceConfig(),
        params: { pageNumber: 1, pageSize: 500 },
        paramsSerializer: serializeParams,
      });
      return response.data.items;
    } catch {
      return [];
    }
  }

  /**
   * Resolve (solve) a service report.
   * POST /v1/customer/service-reports/{number}/solving-report
   */
  async resolveSr(
    number: string,
    data: { solvingDescription: string; costValue?: number; closeDate?: string }
  ): Promise<void> {
    await api.post(
      `/v1/customer/service-reports/${number}/solving-report`,
      data,
      getMachineryConfig()
    );
  }

  /**
   * Delete a service report.
   * DELETE /v1/customer/service-reports/{number}
   */
  async deleteSr(number: string): Promise<void> {
    await api.delete(
      `/v1/customer/service-reports/${number}`,
      getMachineryConfig()
    );
  }

  /**
   * Update (edit) a service report.
   * PUT /v1/customer/service-reports/{number}
   */
  async updateSr(
    number: string,
    data: {
      title: string;
      description: string;
      notes?: string;
      urgency?: number;
      alarmCode?: string;
    }
  ): Promise<void> {
    await api.put(
      `/v1/customer/service-reports/${number}`,
      data,
      getMachineryConfig()
    );
  }

  /**
   * Link an existing SR to an existing WO.
   * POST /v1/customer/work-orders/{workOrderId}/link-service-report
   */
  async linkSrToWorkOrder(
    workOrderId: string,
    data: { serviceReportId: string; serviceReportNumber: string }
  ): Promise<WorkOrder> {
    const response = await api.post<WorkOrder>(
      `/v1/customer/work-orders/${workOrderId}/link-service-report`,
      data,
      getMaintenanceConfig()
    );
    return response.data;
  }

  /**
   * Unlink an SR from a WO (removes junction row).
   * POST /v1/customer/work-orders/{workOrderId}/unlink-service-report
   */
  async unlinkSrFromWorkOrder(
    workOrderId: string,
    serviceReportId: string
  ): Promise<WorkOrder> {
    const response = await api.post<WorkOrder>(
      `/v1/customer/work-orders/${workOrderId}/unlink-service-report`,
      { serviceReportId },
      getMaintenanceConfig()
    );
    return response.data;
  }

  /**
   * Reopen a resolved service report.
   * POST /v1/customer/service-reports/{number}/reopen
   */
  async reopenSr(number: string): Promise<void> {
    await api.post(
      `/v1/customer/service-reports/${number}/reopen`,
      {},
      getMachineryConfig()
    );
  }

  /**
   * Fetch all RFSes (Service Requests) for joining SRs to RFSes via serviceReportNumber.
   * Uses the existing servicing list endpoint.
   */
  async getAllServiceRequests(): Promise<ServiceRequest[]> {
    try {
      // Fetch published + draft RFSes (statusCodes 1-8 covers all)
      const response = await api.get<Page<ServiceRequest>>("/v1/customer/service-requests", {
        ...getServicingConfig(),
        params: {
          statusCodes: [1, 2, 3, 4, 5, 6, 7, 8],
          pageNo: 1,
          pageSize: 500,
        },
        paramsSerializer: serializeParams,
      });
      return response.data.items;
    } catch {
      return [];
    }
  }
}

export default new ServiceReportsApi();
